'use client';

import { useState } from 'react';
import { useTaskActivity, useAddTaskComment } from '@/hooks/useTaskActivity';
import { formatDistanceToNow } from 'date-fns';

interface ActivityFeedProps {
  taskId: number;
}

export default function ActivityFeed({ taskId }: ActivityFeedProps) {
  const [comment, setComment] = useState('');
  const { data: activities = [], isLoading } = useTaskActivity(taskId);
  const addComment = useAddTaskComment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!comment.trim()) {
      return;
    }

    addComment.mutate(
      { taskId, comment },
      {
        onSuccess: () => {
          setComment(''); // Clear input after successful submission
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* Comment Input Form */}
      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment..."
          className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 resize-none"
          rows={3}
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!comment.trim() || addComment.isPending}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addComment.isPending ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>

      {/* Activity List */}
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        {isLoading ? (
          <div className="text-gray-400 text-sm text-center py-4">Loading activity...</div>
        ) : activities.length === 0 ? (
          <div className="text-gray-400 text-sm text-center py-4">
            No activity yet. Be the first to add a comment!
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex gap-3">
              {/* Avatar Circle */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                  {activity.created_by
                    ? `${activity.created_by.user.first_name?.[0] || 'U'}${activity.created_by.user.last_name?.[0] || 'U'}`
                    : 'SY'}
                </div>
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">
                    {activity.created_by
                      ? `${activity.created_by.user.first_name || 'Unknown'} ${activity.created_by.user.last_name || 'User'}`.trim()
                      : 'System'}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {formatDistanceToNow(new Date(activity.date_created), { addSuffix: true })}
                  </span>
                </div>

                <div className="text-gray-300 text-sm mt-1 whitespace-pre-wrap break-words">
                  {activity.comment}
                </div>

                {/* File Badge (if filename exists) */}
                {activity.filename && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#2d1b3d] text-gray-300 text-xs rounded">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      {activity.filename}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
